import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Network,
  Layers,
  Search,
  Plus,
  Edit2,
  Trash2,
  RotateCcw,
  Download,
  Upload,
  CheckCircle2,
  AlertTriangle,
  X,
  ChevronRight,
  Filter,
  FileCode
} from 'lucide-react';
import { taxonomyService, TaxonomyItem } from '../../shared/taxonomyService';
import { useEscapeKey } from '../../shared/useGlobalModalEscape';

export const ReportingTaxonomyWorkspace: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'classification' | 'report_types'>('classification');
  const [taxonomy, setTaxonomy] = useState<TaxonomyItem[]>(() => taxonomyService.getTaxonomy());

  // Subscribe to changes in taxonomyService
  useEffect(() => {
    const unsubscribe = taxonomyService.subscribe(() => {
      setTaxonomy(taxonomyService.getTaxonomy());
    });
    return unsubscribe;
  }, []);

  // Summary counts
  const summary = useMemo(() => taxonomyService.getSummaryCounts(), [taxonomy]);

  // Selected drill-down state
  const areasList = useMemo(() => taxonomyService.getAreas(), [taxonomy]);
  const [selectedArea, setSelectedArea] = useState<string>(() => areasList[0]?.name || 'Air Safety Report (ASR)');

  // Ensure selectedArea is valid
  useEffect(() => {
    if (areasList.length > 0 && !areasList.some((a) => a.name === selectedArea)) {
      setSelectedArea(areasList[0].name);
    }
  }, [areasList, selectedArea]);

  const kindsList = useMemo(() => taxonomyService.getKindsForArea(selectedArea), [taxonomy, selectedArea]);
  const [selectedKind, setSelectedKind] = useState<string>(() => kindsList[0]?.name || 'Occurrence Report');

  // Ensure selectedKind is valid
  useEffect(() => {
    if (kindsList.length > 0 && !kindsList.some((k) => k.name === selectedKind)) {
      setSelectedKind(kindsList[0].name);
    }
  }, [kindsList, selectedKind]);

  const typesList = useMemo(
    () => taxonomyService.getTypesForAreaAndKind(selectedArea, selectedKind),
    [taxonomy, selectedArea, selectedKind]
  );
  const [selectedType, setSelectedType] = useState<string>(() => typesList[0]?.name || 'Air Traffic Management');

  // Ensure selectedType is valid
  useEffect(() => {
    if (typesList.length > 0 && !typesList.some((t) => t.name === selectedType)) {
      setSelectedType(typesList[0].name);
    }
  }, [typesList, selectedType]);

  const classificationsList = useMemo(
    () => taxonomyService.getClassifications(selectedArea, selectedKind, selectedType),
    [taxonomy, selectedArea, selectedKind, selectedType]
  );

  // Search filters for each column
  const [searchArea, setSearchArea] = useState<string>('');
  const [searchKind, setSearchKind] = useState<string>('');
  const [searchType, setSearchType] = useState<string>('');
  const [searchClassification, setSearchClassification] = useState<string>('');

  // Modals state
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showJsonModal, setShowJsonModal] = useState<boolean>(false);
  const [jsonText, setJsonText] = useState<string>('');
  const [feedbackMsg, setFeedbackMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  useEscapeKey(() => {
    if (showAddModal) setShowAddModal(false);
    if (showJsonModal) setShowJsonModal(false);
  }, showAddModal || showJsonModal);

  // Add Item form state
  const [newArea, setNewArea] = useState<string>('');
  const [newKind, setNewKind] = useState<string>('');
  const [newType, setNewType] = useState<string>('');
  const [newClassification, setNewClassification] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showNotification = (text: string, type: 'success' | 'error' = 'success') => {
    setFeedbackMsg({ text, type });
    setTimeout(() => setFeedbackMsg(null), 3800);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      if (text) {
        const res = taxonomyService.importFromJSON(text);
        if (res.success) {
          showNotification(`Imported ${res.count} taxonomy items successfully from ${file.name}!`);
        } else {
          alert(`Failed to import JSON: ${res.error}`);
        }
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleOpenAddModal = () => {
    setNewArea(selectedArea);
    setNewKind(selectedKind);
    setNewType(selectedType);
    setNewClassification('');
    setShowAddModal(true);
  };

  const handleSaveNewItem = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      taxonomyService.addTaxonomyItem({
        area: newArea,
        kind: newKind,
        type: newType,
        classification: newClassification
      });
      setShowAddModal(false);
      showNotification(`Added classification "${newClassification}" successfully.`);
    } catch (err: any) {
      alert(err.message || 'Failed to add item');
    }
  };

  const handleDeleteItem = (classification: string) => {
    if (window.confirm(`Delete "${classification}" from taxonomy?`)) {
      taxonomyService.deleteTaxonomyItem({
        area: selectedArea,
        kind: selectedKind,
        type: selectedType,
        classification
      });
      showNotification(`Deleted "${classification}" from taxonomy.`);
    }
  };

  const handleOpenJsonModal = () => {
    setJsonText(taxonomyService.exportToJSON());
    setShowJsonModal(true);
  };

  const handleSaveJson = () => {
    const res = taxonomyService.importFromJSON(jsonText);
    if (res.success) {
      setShowJsonModal(false);
      showNotification(`Taxonomy updated successfully! Loaded ${res.count} items.`);
    } else {
      alert(`JSON Error: ${res.error}`);
    }
  };

  const handleExportJson = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(taxonomyService.exportToJSON());
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', 'raport_taxo.json');
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    showNotification('Exported taxonomy JSON successfully.');
  };

  const handleResetToDefault = () => {
    if (window.confirm('Reset taxonomy to standard 1,345 IQSMS items from default file? Any custom additions will be reverted.')) {
      taxonomyService.resetTaxonomy();
      showNotification('Taxonomy successfully reset to default 1,345 items.');
    }
  };

  // Filtered lists
  const filteredAreas = areasList.filter((a) => a.name.toLowerCase().includes(searchArea.toLowerCase()));
  const filteredKinds = kindsList.filter((k) => k.name.toLowerCase().includes(searchKind.toLowerCase()));
  const filteredTypes = typesList.filter((t) => t.name.toLowerCase().includes(searchType.toLowerCase()));
  const filteredClassifications = classificationsList.filter((c) =>
    c.toLowerCase().includes(searchClassification.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Toast Notification */}
      {feedbackMsg && (
        <div
          style={{
            padding: '12px 18px',
            borderRadius: '8px',
            backgroundColor: feedbackMsg.type === 'success' ? '#ecfdf5' : '#fef2f2',
            color: feedbackMsg.type === 'success' ? '#065f46' : '#991b1b',
            border: `1px solid ${feedbackMsg.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            fontWeight: 600
          }}
        >
          {feedbackMsg.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          <span>{feedbackMsg.text}</span>
        </div>
      )}

      {/* Sub Tabs Bar (Report Clasification 1345 & Report Type 11) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setActiveTab('classification')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '999px',
              border: 'none',
              backgroundColor: activeTab === 'classification' ? '#0f172a' : '#ffffff',
              color: activeTab === 'classification' ? '#ffffff' : '#475569',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: activeTab === 'classification' ? '0 2px 6px rgba(15, 23, 42, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'all 0.15s ease'
            }}
          >
            <Network size={15} />
            <span>Report Clasification</span>
            <span
              style={{
                fontSize: '11px',
                padding: '2px 7px',
                borderRadius: '999px',
                backgroundColor: activeTab === 'classification' ? '#334155' : '#f1f5f9',
                color: activeTab === 'classification' ? '#ffffff' : '#64748b',
                fontWeight: 700
              }}
            >
              {summary.classificationsCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('report_types')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '999px',
              border: 'none',
              backgroundColor: activeTab === 'report_types' ? '#0f172a' : '#ffffff',
              color: activeTab === 'report_types' ? '#ffffff' : '#475569',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: activeTab === 'report_types' ? '0 2px 6px rgba(15, 23, 42, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'all 0.15s ease'
            }}
          >
            <Layers size={15} />
            <span>Report Type</span>
            <span
              style={{
                fontSize: '11px',
                padding: '2px 7px',
                borderRadius: '999px',
                backgroundColor: activeTab === 'report_types' ? '#334155' : '#f1f5f9',
                color: activeTab === 'report_types' ? '#ffffff' : '#64748b',
                fontWeight: 700
              }}
            >
              11
            </span>
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginRight: '6px' }}>
            Standardized IQSMS Event Classification & Taxonomy Hierarchy
          </span>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json,.txt"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />

          <button
            type="button"
            className="dash-ind-action-btn"
            onClick={handleOpenAddModal}
            title="Add New Taxonomy Classification"
            style={{ backgroundColor: '#2563eb', color: '#ffffff', borderColor: '#2563eb' }}
          >
            <Plus size={13} />
            <span>Add Item</span>
          </button>

          <button
            type="button"
            className="dash-ind-action-btn"
            onClick={handleOpenJsonModal}
            title="Revise via JSON editor"
          >
            <FileCode size={13} />
            <span>Edit JSON</span>
          </button>

          <button
            type="button"
            className="dash-ind-action-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Upload taxonomy JSON/TXT file"
          >
            <Upload size={13} />
            <span>Upload File</span>
          </button>

          <button
            type="button"
            className="dash-ind-action-btn"
            onClick={handleExportJson}
            title="Export JSON file"
          >
            <Download size={13} />
            <span>Export</span>
          </button>

          <button
            type="button"
            className="dash-ind-action-btn"
            onClick={handleResetToDefault}
            title="Reset to default 1345 items"
          >
            <RotateCcw size={13} />
            <span>Reset</span>
          </button>
        </div>
      </div>

      {activeTab === 'classification' && (
        <>
          {/* Summary KPI Cards Row (Matching Screenshot: 5, 6, 109, 1345) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '16px'
            }}
          >
            {/* Card 1: AREAS OF OCCURRENCE */}
            <div
              className="dash-card"
              style={{ padding: '18px 20px', borderRadius: '16px', border: '1px solid #edf0f6' }}
            >
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em' }}>
                AREAS OF OCCURRENCE
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', margin: '6px 0 2px 0' }}>
                {summary.areasCount}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Primary reporting domains</div>
            </div>

            {/* Card 2: KINDS OF REPORT */}
            <div
              className="dash-card"
              style={{ padding: '18px 20px', borderRadius: '16px', border: '1px solid #edf0f6' }}
            >
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em' }}>
                KINDS OF REPORT
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#2563eb', margin: '6px 0 2px 0' }}>
                {summary.kindsCount}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: '#64748b',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={summary.kindsList.join(' • ')}
              >
                {summary.kindsList.join(' • ')}
              </div>
            </div>

            {/* Card 3: TYPES OF OCCURRENCE */}
            <div
              className="dash-card"
              style={{ padding: '18px 20px', borderRadius: '16px', border: '1px solid #edf0f6' }}
            >
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em' }}>
                TYPES OF OCCURRENCE
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', margin: '6px 0 2px 0' }}>
                {summary.typesCount}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Operational category groups</div>
            </div>

            {/* Card 4: EVENT CLASSIFICATIONS */}
            <div
              className="dash-card"
              style={{ padding: '18px 20px', borderRadius: '16px', border: '1px solid #edf0f6' }}
            >
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em' }}>
                EVENT CLASSIFICATIONS
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#10b981', margin: '6px 0 2px 0' }}>
                {summary.classificationsCount}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total IQSMS taxonomy items</div>
            </div>
          </div>

          {/* IQSMS Taxonomy Hierarchy Explorer Card (Matching Screenshot Explorer) */}
          <div
            className="dash-card"
            style={{ padding: '22px 24px', borderRadius: '16px', border: '1px solid #edf0f6' }}
          >
            {/* Header with Title and Breadcrumb */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '18px',
                paddingBottom: '14px',
                borderBottom: '1px solid #f1f5f9',
                flexWrap: 'wrap',
                gap: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '9px',
                    backgroundColor: '#eff6ff',
                    color: '#2563eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Network size={19} />
                </div>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                    IQSMS Taxonomy Hierarchy Explorer
                  </h3>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0 0' }}>
                    Drill down through Area of Occurrence → Kind of Report → Type of Occurrence → Event Classification
                  </p>
                </div>
              </div>

              {/* Breadcrumb Path on the right */}
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  color: '#1d4ed8',
                  padding: '5px 12px',
                  borderRadius: '999px',
                  fontSize: '12px',
                  fontWeight: 600
                }}
              >
                <span>{selectedArea}</span>
                <span>→</span>
                <span>{selectedKind}</span>
                <span>→</span>
                <span>{selectedType}</span>
                <span
                  style={{
                    backgroundColor: '#1d4ed8',
                    color: '#ffffff',
                    padding: '1px 6px',
                    borderRadius: '999px',
                    fontSize: '10.5px'
                  }}
                >
                  {classificationsList.length}
                </span>
              </div>
            </div>

            {/* 4 Columns Hierarchy Explorer Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '14px',
                height: '480px',
                maxHeight: '480px'
              }}
            >
              {/* Column 1: AREA OF OCCURRENCE */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  backgroundColor: '#fafbfc',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '480px',
                  maxHeight: '480px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    padding: '10px 14px',
                    backgroundColor: '#f1f5f9',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                    1. Area of Occurrence
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#2563eb',
                      backgroundColor: '#eff6ff',
                      padding: '1px 6px',
                      borderRadius: '4px'
                    }}
                  >
                    {areasList.length}
                  </span>
                </div>

                <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ position: 'relative' }}>
                    <Search
                      size={13}
                      style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
                    />
                    <input
                      type="text"
                      placeholder="Filter area..."
                      value={searchArea}
                      onChange={(e) => setSearchArea(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '4px 8px 4px 26px',
                        fontSize: '11.5px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: '#ffffff'
                      }}
                    />
                  </div>
                </div>

                <div className="card-scroll-pane" style={{ flex: 1, overflowY: 'auto', padding: '8px', minHeight: 0 }}>
                  {filteredAreas.map((item) => {
                    const isSelected = item.name === selectedArea;
                    return (
                      <div
                        key={item.name}
                        onClick={() => setSelectedArea(item.name)}
                        style={{
                          padding: '9px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: isSelected ? 700 : 500,
                          backgroundColor: isSelected ? '#2563eb' : 'transparent',
                          color: isSelected ? '#ffffff' : '#1e293b',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '4px',
                          transition: 'all 0.12s ease'
                        }}
                      >
                        <span style={{ lineHeight: 1.3 }}>{item.name}</span>
                        <span
                          style={{
                            fontSize: '10.5px',
                            fontWeight: 700,
                            padding: '1px 6px',
                            borderRadius: '999px',
                            backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                            color: isSelected ? '#ffffff' : '#64748b'
                          }}
                        >
                          {item.count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Column 2: KIND OF REPORT */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  backgroundColor: '#fafbfc',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '480px',
                  maxHeight: '480px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    padding: '10px 14px',
                    backgroundColor: '#f1f5f9',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                    2. Kind of Report
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#2563eb',
                      backgroundColor: '#eff6ff',
                      padding: '1px 6px',
                      borderRadius: '4px'
                    }}
                  >
                    {kindsList.length}
                  </span>
                </div>

                <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ position: 'relative' }}>
                    <Search
                      size={13}
                      style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
                    />
                    <input
                      type="text"
                      placeholder="Filter kind..."
                      value={searchKind}
                      onChange={(e) => setSearchKind(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '4px 8px 4px 26px',
                        fontSize: '11.5px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: '#ffffff'
                      }}
                    />
                  </div>
                </div>

                <div className="card-scroll-pane" style={{ flex: 1, overflowY: 'auto', padding: '8px', minHeight: 0 }}>
                  {filteredKinds.map((item) => {
                    const isSelected = item.name === selectedKind;
                    return (
                      <div
                        key={item.name}
                        onClick={() => setSelectedKind(item.name)}
                        style={{
                          padding: '9px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: isSelected ? 700 : 500,
                          backgroundColor: isSelected ? '#2563eb' : 'transparent',
                          color: isSelected ? '#ffffff' : '#1e293b',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '4px',
                          transition: 'all 0.12s ease'
                        }}
                      >
                        <span style={{ lineHeight: 1.3 }}>{item.name}</span>
                        <span
                          style={{
                            fontSize: '10.5px',
                            fontWeight: 700,
                            padding: '1px 6px',
                            borderRadius: '999px',
                            backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                            color: isSelected ? '#ffffff' : '#64748b'
                          }}
                        >
                          {item.count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Column 3: TYPE OF OCCURRENCE */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  backgroundColor: '#fafbfc',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '480px',
                  maxHeight: '480px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    padding: '10px 14px',
                    backgroundColor: '#f1f5f9',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                    3. Type of Occurrence
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#2563eb',
                      backgroundColor: '#eff6ff',
                      padding: '1px 6px',
                      borderRadius: '4px'
                    }}
                  >
                    {typesList.length}
                  </span>
                </div>

                <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ position: 'relative' }}>
                    <Search
                      size={13}
                      style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
                    />
                    <input
                      type="text"
                      placeholder="Filter type..."
                      value={searchType}
                      onChange={(e) => setSearchType(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '4px 8px 4px 26px',
                        fontSize: '11.5px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: '#ffffff'
                      }}
                    />
                  </div>
                </div>

                <div className="card-scroll-pane" style={{ flex: 1, overflowY: 'auto', padding: '8px', minHeight: 0 }}>
                  {filteredTypes.map((item) => {
                    const isSelected = item.name === selectedType;
                    return (
                      <div
                        key={item.name}
                        onClick={() => setSelectedType(item.name)}
                        style={{
                          padding: '9px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: isSelected ? 700 : 500,
                          backgroundColor: isSelected ? '#2563eb' : 'transparent',
                          color: isSelected ? '#ffffff' : '#1e293b',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '4px',
                          transition: 'all 0.12s ease'
                        }}
                      >
                        <span style={{ lineHeight: 1.3 }}>{item.name}</span>
                        <span
                          style={{
                            fontSize: '10.5px',
                            fontWeight: 700,
                            padding: '1px 6px',
                            borderRadius: '999px',
                            backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                            color: isSelected ? '#ffffff' : '#64748b'
                          }}
                        >
                          {item.count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Column 4: EVENT CLASSIFICATION */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '480px',
                  maxHeight: '480px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    padding: '10px 14px',
                    backgroundColor: '#f1f5f9',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                    4. Event Classification
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#10b981',
                      backgroundColor: '#ecfdf5',
                      padding: '1px 6px',
                      borderRadius: '4px'
                    }}
                  >
                    {classificationsList.length}
                  </span>
                </div>

                <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ position: 'relative' }}>
                    <Search
                      size={13}
                      style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
                    />
                    <input
                      type="text"
                      placeholder="Filter classifications..."
                      value={searchClassification}
                      onChange={(e) => setSearchClassification(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '4px 8px 4px 26px',
                        fontSize: '11.5px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: '#ffffff'
                      }}
                    />
                  </div>
                </div>

                <div className="card-scroll-pane" style={{ flex: 1, overflowY: 'auto', padding: '8px', minHeight: 0 }}>
                  {filteredClassifications.length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', fontSize: '12px', color: '#94a3b8' }}>
                      No classifications found.
                    </div>
                  ) : (
                    filteredClassifications.map((item) => (
                      <div
                        key={item}
                        className="taxonomy-item-row"
                        style={{
                          padding: '9px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          color: '#1e293b',
                          backgroundColor: '#ffffff',
                          border: '1px solid #f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '4px',
                          transition: 'all 0.12s ease'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                          <span
                            style={{
                              width: '7px',
                              height: '7px',
                              borderRadius: '50%',
                              backgroundColor: '#10b981',
                              flexShrink: 0
                            }}
                          />
                          <span style={{ fontWeight: 500, lineHeight: 1.35 }}>{item}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(item)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title={`Delete ${item}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Sub Tab: REPORT TYPE 11 */}
      {activeTab === 'report_types' && (
        <div className="dash-card" style={{ padding: '24px', borderRadius: '16px', border: '1px solid #edf0f6' }}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}>
              Standardized Report Types (11)
            </h3>
            <p style={{ fontSize: '12.5px', color: '#64748b', margin: 0 }}>
              Primary top-level intake forms matching flight operations, engineering, ramp, safety and security domains.
            </p>
          </div>

          <div className="dash-ind-modal-table-wrap card-scroll-pane" style={{ maxHeight: '520px', overflowY: 'auto' }}>
            <table className="dash-ind-modal-table">
              <thead>
                <tr>
                  <th>Type Code</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { code: 'AIR_OPS', title: 'Pilot Safety Report', cat: 'SAFETY', desc: 'In-flight occurrences, cockpit observations, approach deviations' },
                  { code: 'MAINTENANCE', title: 'Engineering and Maintenance Occurrence Report', cat: 'SAFETY', desc: 'Aircraft defects, technical log book entries, MEL deviations' },
                  { code: 'GROUND_OPS', title: 'Ground and Cargo Operations Occurrence Report', cat: 'SAFETY', desc: 'Ramp safety, baggage belt operations, pushback incidents' },
                  { code: 'SECURITY', title: 'Security Report', cat: 'SAFETY', desc: 'Security breaches, unverified personnel access, unruly passengers' },
                  { code: 'WILDLIFE_BIRD_STRIKE', title: 'Bird Strike Report', cat: 'SAFETY', desc: 'Bird and wildlife impact during takeoff, climb, flare, or landing' },
                  { code: 'FDP_EXTENSION', title: 'Extension of Flight Duty Period', cat: 'SAFETY', desc: 'Commander discretion exercised for flight duty extension under EASA ORO.FTL' },
                  { code: 'CABIN_SAFETY', title: 'Cabin Safety Report', cat: 'SAFETY', desc: 'Cabin crew injury, turbulence, emergency equipment anomalies' },
                  { code: 'TECHNICAL_FACILITIES', title: 'TS & Facilities Report', cat: 'NON_SAFETY', desc: 'Hangar, technical store, office, or infrastructure discrepancies' },
                  { code: 'CONFIDENTIAL', title: 'Confidential Report', cat: 'SAFETY', desc: 'Restricted de-identified reporting directly to Safety Manager' },
                  { code: 'VOLUNTARY_HAZARD', title: 'Voluntary Report', cat: 'SAFETY', desc: 'Proactive hazard identification and near-miss observations' },
                  { code: 'FATIGUE', title: 'Fatigue Report', cat: 'SAFETY', desc: 'Fatigue risk management report, cumulative sleep debt or circadian disruption' }
                ].map((item) => (
                  <tr key={item.code}>
                    <td>
                      <span className="dash-ind-ref-badge">{item.code}</span>
                    </td>
                    <td>
                      <span style={{ fontWeight: 600, color: '#0f172a' }}>{item.title}</span>
                    </td>
                    <td>
                      <span className={`dash-ind-badge ${item.cat === 'SAFETY' ? 'dash-ind-badge-blue' : 'dash-ind-badge-gray'}`}>
                        {item.cat}
                      </span>
                    </td>
                    <td style={{ fontSize: '12px', color: '#64748b' }}>{item.desc}</td>
                    <td>
                      <span className="dash-ind-badge dash-ind-badge-green">ACTIVE</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Classification Modal */}
      {showAddModal && (
        <div className="cal-modal-backdrop" onClick={() => setShowAddModal(false)}>
          <div
            className="cal-modal-container"
            style={{ maxWidth: '520px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cal-modal-header">
              <div className="cal-modal-title-group">
                <div className="cal-modal-icon-wrap">
                  <Plus size={18} color="#2563eb" />
                </div>
                <div>
                  <h3 className="cal-modal-title" style={{ margin: 0 }}>Add Classification Item</h3>
                  <p className="cal-modal-subtitle">Add a new event classification to the standardized taxonomy hierarchy</p>
                </div>
              </div>
              <button type="button" className="cal-modal-close-btn" onClick={() => setShowAddModal(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveNewItem} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  1. Area of Occurrence
                </label>
                <input
                  type="text"
                  value={newArea}
                  onChange={(e) => setNewArea(e.target.value)}
                  required
                  style={{ width: '100%', padding: '8px 12px', fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  2. Kind of Report
                </label>
                <input
                  type="text"
                  value={newKind}
                  onChange={(e) => setNewKind(e.target.value)}
                  required
                  style={{ width: '100%', padding: '8px 12px', fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  3. Type of Occurrence
                </label>
                <input
                  type="text"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  required
                  style={{ width: '100%', padding: '8px 12px', fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  4. Event Classification Title
                </label>
                <input
                  type="text"
                  placeholder="e.g. Inadequate Separation (Loss of Standard)"
                  value={newClassification}
                  onChange={(e) => setNewClassification(e.target.value)}
                  required
                  autoFocus
                  style={{ width: '100%', padding: '8px 12px', fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  className="dash-btn dash-btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="dash-btn dash-btn-primary"
                >
                  Save Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* JSON Editor / Revision Modal */}
      {showJsonModal && (
        <div className="cal-modal-backdrop" onClick={() => setShowJsonModal(false)}>
          <div
            className="cal-modal-container"
            style={{ maxWidth: '840px', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cal-modal-header">
              <div className="cal-modal-title-group">
                <div className="cal-modal-icon-wrap">
                  <FileCode size={18} color="#2563eb" />
                </div>
                <div>
                  <h3 className="cal-modal-title" style={{ margin: 0 }}>Revise Organization Taxonomy (JSON)</h3>
                  <p className="cal-modal-subtitle">Paste, edit or validate the complete taxonomy array ({taxonomy.length} entries)</p>
                </div>
              </div>
              <button type="button" className="cal-modal-close-btn" onClick={() => setShowJsonModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '16px 24px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: '380px' }}>
              <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 10px 0' }}>
                Each item must be an object with: <code>&#123; "area": "...", "kind": "...", "type": "...", "classification": "..." &#125;</code>.
              </p>
              <textarea
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                style={{
                  flex: 1,
                  width: '100%',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '12px',
                  padding: '12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  outline: 'none',
                  backgroundColor: '#0f172a',
                  color: '#f8fafc',
                  resize: 'none'
                }}
              />
            </div>

            <div
              style={{
                padding: '14px 24px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f8fafc'
              }}
            >
              <button
                type="button"
                className="dash-btn dash-btn-secondary"
                onClick={() => setShowJsonModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dash-btn dash-btn-primary"
                onClick={handleSaveJson}
              >
                Validate & Save JSON Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
